// Source of truth: modules/support/domain/status-fsm.ts. Keep in sync (a shared
// contract export is a follow-up) — that module lives in `modules/support/domain/`,
// a Gate 7 module-private directory not exported via `package.json`, so it is not
// importable from the admin web bundle regardless of the sync question.
import type { BadgeVariant } from '@heyroot/ui/primitives/Badge';

export type FeedbackStatus =
  | 'submitted'
  | 'under_review'
  | 'planned'
  | 'in_progress'
  | 'completed'
  | 'declined';

export const ALL_STATUSES: readonly FeedbackStatus[] = [
  'submitted',
  'under_review',
  'planned',
  'in_progress',
  'completed',
  'declined',
];

const TRANSITIONS: Record<FeedbackStatus, readonly FeedbackStatus[]> = {
  submitted: ['under_review', 'declined'],
  under_review: ['planned', 'in_progress', 'completed', 'declined'],
  planned: ['in_progress', 'completed', 'declined', 'under_review'],
  in_progress: ['completed', 'declined', 'planned', 'under_review'],
  completed: ['under_review'],
  declined: ['under_review'],
};

/** Finished statuses: a note for the submitter exists only on the move INTO one of these (mirrors TERMINAL_STATUSES in modules/support/domain/status-fsm.ts). */
export function isTerminalStatus(status: string): boolean {
  return status === 'completed' || status === 'declined';
}

/** Legal next statuses from `from` (does not include `from` itself — callers add the "no change" option separately). */
export function legalNextStatuses(from: FeedbackStatus): readonly FeedbackStatus[] {
  return TRANSITIONS[from] ?? [];
}

export const STATUS_LABELS: Record<FeedbackStatus, string> = {
  submitted: 'Submitted',
  under_review: 'Under review',
  planned: 'Planned',
  in_progress: 'In progress',
  completed: 'Completed',
  declined: 'Declined',
};

/** Display label for a status; falls back to the raw value for any unrecognized string. */
export function statusLabel(status: string): string {
  return (STATUS_LABELS as Record<string, string>)[status] ?? status;
}

/** Badge tone for a status — used by both the inbox table and the detail header. */
export function statusBadgeVariant(status: string): BadgeVariant {
  switch (status) {
    case 'submitted':
      return 'info';
    case 'under_review':
    case 'in_progress':
      return 'warning';
    case 'planned':
      return 'neutral';
    case 'completed':
      return 'success';
    case 'declined':
      return 'danger';
    default:
      return 'neutral';
  }
}

/** Badge tone for a priority — shared by the inbox column and the detail header. */
export function priorityVariant(priority: string): BadgeVariant {
  if (priority === 'critical') return 'danger';
  if (priority === 'high') return 'warning';
  if (priority === 'medium') return 'info';
  return 'neutral';
}

/** Compact age ("5m", "3h", "12d") of an ISO instant relative to `now`. */
export function formatAge(iso: string, now: number = Date.now()): string {
  const ms = Math.max(0, now - new Date(iso).getTime());
  const min = Math.floor(ms / 60_000);
  if (min < 60) return `${min}m`;
  const hr = Math.floor(min / 60);
  if (hr < 48) return `${hr}h`;
  return `${Math.floor(hr / 24)}d`;
}

/** Statuses where nobody has acted yet / work is open: an old item here needs attention. */
const OPEN_STATUSES = new Set<string>(['submitted', 'under_review']);
export const STALE_AFTER_DAYS = 7;

/** True when an item is still waiting on triage and has sat for STALE_AFTER_DAYS or more. */
export function isStale(status: string, iso: string, now: number = Date.now()): boolean {
  return (
    OPEN_STATUSES.has(status) && now - new Date(iso).getTime() >= STALE_AFTER_DAYS * 86_400_000
  );
}

/** Badge tone for a request type — used by both the inbox table and the detail header. */
export function requestTypeVariant(requestType: string): BadgeVariant {
  switch (requestType) {
    case 'bug':
      return 'danger';
    case 'feature':
      return 'success';
    case 'enhancement':
      return 'info';
    default:
      return 'neutral';
  }
}
